import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BatteryFullIcon, BuildingIcon, CheckIcon, RefreshCwIcon, TruckIcon } from 'lucide-react';
import { ObjectionId, objectionChoices, RECOVERY_ID } from '../data/objections';
import { offers } from '../data/product';
import { RX_INSERT_UPDATES_URL, RX_INSERT_FALLBACK_MAILTO } from '../data/rxInsert';
import { CtaButton } from './CtaButton';
import { LifetimeSeal } from './LifetimeSeal';
import { useTrackOnView } from '../hooks/useTrackOnView';
import { trackCustom, packEvents } from '../utils/tracking';
import { requestOffer, PackId } from '../utils/offerBus';
interface ObjectionRecoveryProps {
  /** The objection the visitor picked. Nothing renders until they pick one. */
  active: ObjectionId | null;
  /** Reopens the selector. */
  onChooseAnother: () => void;
}
const headlineClass = 'grit-headline text-[clamp(2.25rem,8vw,4.5rem)] font-extrabold uppercase leading-[0.92] text-offwhite';
const subClass = 'mt-5 max-w-2xl text-[15px] leading-relaxed text-steel-300 sm:text-lg';
const statementClass = 'mt-6 border-l-4 border-grit-500 pl-4 font-display text-2xl font-bold uppercase leading-tight text-offwhite sm:text-3xl';
function Bullets({
  items
}: {
  items: string[];
}) {
  return <ul className="mt-6 grid gap-2.5" data-id="element-675" data-mp="29xbocb5dxf1f">
      {items.map(line => <li key={line} className="flex items-start gap-2.5 text-[15px] font-medium text-offwhite" data-id="element-676" data-mp="2xll7f5b7roov">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-677" data-mp="32lr10r11nl9l" />
          {line}
        </li>)}
    </ul>;
}
const scrollToCompany = () => {
  document.getElementById('company')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
};

/** The public packs only. Private pricing never appears here. */
function PackClose({
  recommended,
  cta
}: {
  recommended: PackId;
  cta: string;
}) {
  return <div className="mt-10 border-t border-white/10 pt-8" data-id="element-678" data-mp="3lch52j2cpe41">
      <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-679" data-mp="3v7fnlw35u5a1">Recommended for you</p>
      <ul className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-680" data-mp="3q834nmfeticq">
        {offers.map(offer => {
        const isPick = offer.id === recommended;
        return <li key={offer.id} data-id="element-681" data-mp="3m93xrwa4bbin">
              <a href="#offer" data-buy-intent="true" onClick={event => {
            event.preventDefault();
            requestOffer(offer.id);
            trackCustom(packEvents[offer.id].selected);
            trackCustom('mini_funnel_cta_clicked', {
              pack: offer.id
            });
          }} className={`flex h-full flex-col border p-4 transition-[border-color,background-color] duration-150 ease-grit focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${isPick ? 'border-grit-500 bg-grit-500/10 hover:bg-grit-500/15' : 'border-white/15 bg-ink-900 hover:border-white/35'}`} data-id="element-682" data-mp="3h8fyknbqjodc">
                <span className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-683" data-mp="140f22nwfn0gl">
                  {offer.eyebrow ?? offer.kicker}
                </span>
                <span className="font-display text-2xl font-bold uppercase leading-none text-offwhite" data-id="element-684" data-mp="1dz4j6egi8gvs">{offer.units}</span>
                <span className="mt-2 font-display text-3xl font-extrabold leading-none text-offwhite" data-id="element-685" data-mp="1hzojfqwdk0m5">{offer.price}</span>
                <span className="mt-1 text-[12px] font-semibold text-steel-400" data-id="element-686" data-mp="l8m7ip1pt76r">
                  {offer.perUnit ?? 'One-year coverage · lifetime available'}
                </span>
                <span className="mt-auto pt-3 text-[12px] font-semibold text-steel-400" data-id="element-687" data-mp="u9u6l9c22c1w">
                  Free U.S. shipping
                </span>
              </a>
            </li>;
      })}
      </ul>
      <CtaButton offer={recommended} className="mt-6 w-full sm:w-auto" onClick={() => trackCustom('mini_funnel_cta_clicked')} data-id="element-688" data-mp="2jz5kfeu4xxe">
        {cta}
      </CtaButton>
    </div>;
}
function PriceFunnel() {
  const splitRef = useTrackOnView<HTMLDivElement>('price_split_offer_viewed');
  return <>
      <h2 className={headlineClass} data-id="element-689" data-mp="3epbtt4ha1nj5">
        Here&apos;s what
        <br data-id="element-690" data-mp="3tdgfmhbmmf65" />
        <span className="text-grit-500" data-id="element-691" data-mp="3td6mwlz75twr">$49.95 gets you.</span>
      </h2>
      <p className={subClass} data-id="element-692" data-mp="3ol574q2tll1z">
        Not a pair of glasses. A complete work system: light, three lenses and everything to carry and care for it.
      </p>
      <div className="mt-8 grid gap-8 lg:grid-cols-2" data-id="element-693" data-mp="69s42fften8u">
        <Bullets items={['GritVue X1 frame with built-in 800-lumen light', '3 interchangeable lenses: Clear, Amber, Smoke', 'Hard protective case AV8, protective pouch and cleaning cloth', 'Charging cable and GritVue Quick Start Guide', 'One-year replacement coverage, or Lifetime on multi-packs']} data-id="element-694" data-mp="17ko7erv6n18" />
        <div ref={splitRef} className="border border-white/12 bg-ink-900 p-6" data-id="element-695" data-mp="2cyx19zmcaj9m">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-696" data-mp="sgcma2t4brub">Cost per X1</p>
          <dl className="mt-4 grid gap-2.5 text-[15px]" data-id="element-697" data-mp="xfpbgu7gpb02">
            {[['1 X1', '$49.95'], ['3 X1 · Buy 2, get 1 free', '$33.30 each'], ['6 X1 · Buy 4, get 2 free', '$29.98 each'], ['30 X1 · Buy 20, get 10 free', '$23.33 each']].map(([label, value]) => <div key={label} className="flex items-baseline justify-between gap-4 border-b border-white/5 pb-2" data-id="element-698" data-mp="9otny9a1h1ym">
                <dt className="text-steel-300" data-id="element-699" data-mp="dsj9sqzbx5fy">{label}</dt>
                <dd className="font-display text-2xl font-extrabold text-offwhite" data-id="element-700" data-mp="3vxmismsf21ln">{value}</dd>
              </div>)}
          </dl>
          <p className="mt-4 text-[14px] leading-relaxed text-steel-300" data-id="element-701" data-mp="4qs6fss9hap9">
            Work with a crew? Split a 3-pack with two coworkers and each of you pays about $33.30, with lifetime coverage
            on all three.
          </p>
        </div>
      </div>
      <CtaButton offer="trio" className="mt-8 w-full sm:w-auto" onClick={() => {
      trackCustom('price_split_clicked');
      trackCustom('mini_funnel_cta_clicked');
    }} data-id="element-702" data-mp="1xnnnws0l3p3i">
        See the best value for me
      </CtaButton>
    </>;
}
function PrescriptionFunnel() {
  const updatesHref = RX_INSERT_UPDATES_URL || RX_INSERT_FALLBACK_MAILTO;
  return <>
      <h2 className={headlineClass} data-id="element-703" data-mp="2gctyfrr85ae8">
        Wear prescription lenses?
        <br data-id="element-704" data-mp="2bf2b7b1iycgd" />
        <span className="text-grit-500" data-id="element-705" data-mp="2beipdj8r73x2">Your X1 is prescription ready.</span>
      </h2>
      <p className={subClass} data-id="element-706" data-mp="26nsig1koit09">
        Take your X1 to your trusted optical professional and ask about a custom prescription setup. You can buy and
        use it today.
      </p>
      <div className="mt-8 grid gap-4 lg:grid-cols-2" data-id="element-707" data-mp="2z4d1fyl90og9">
        <div className="border-2 border-grit-500 bg-grit-500/10 p-6" data-id="element-708" data-mp="2z54xtbmo6q7l">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-709" data-mp="2ud4v5bf7tj7b">Available now</p>
          <p className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-offwhite" data-id="element-710" data-mp="32n2g0uwnlvmc">Prescription ready</p>
          <p className="mt-2 text-[15px] leading-relaxed text-steel-300" data-id="element-711" data-mp="36onq68hszvsu">Your vision. Your prescription. Your X1.</p>
        </div>
        <div className="border border-white/12 bg-ink-900 p-6" data-id="element-712" data-mp="3bp1ubfgaalll">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-713" data-mp="3gmv0i8hl7flv">In development</p>
          <p className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-offwhite" data-id="element-714" data-mp="3lkn4i0vias6f">GritVue X1 RX Insert™</p>
          <p className="mt-2 text-[15px] leading-relaxed text-steel-300" data-id="element-715" data-mp="3pknkvr261dg4">
            A removable prescription insert designed to sit behind the main lens, so your prescription stays in place while
            you switch Clear, Amber and Smoke. Prototype in development. Not required to use your X1.
          </p>
          <a href={updatesHref} onClick={() => trackCustom('rx_insert_updates_clicked')} className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.18em] text-grit-500 underline-offset-4 hover:underline" data-id="element-716" data-mp="d5cw6b10wqq7">
            Get RX Insert updates
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-717" data-mp="17hhxgltt921t" />
          </a>
        </div>
      </div>
      <p className="mt-3 text-[12px] text-steel-500" data-id="element-718" data-mp="12hn66h64iag8">
        <Link to="/legal/terms" className="underline" data-id="element-719" data-mp="12fsodbq5eygd">
          Terms apply.
        </Link>
      </p>
      <CtaButton offer="single" className="mt-8 w-full sm:w-auto" onClick={() => trackCustom('mini_funnel_cta_clicked')} data-id="element-720" data-mp="epy8elaj8trw">
        Get my X1
      </CtaButton>
    </>;
}
function BatteryFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-721" data-mp="jrnm68p53ljo">
        Light that lasts
        <br data-id="element-722" data-mp="jq2ssmeka8ne" />
        <span className="text-grit-500" data-id="element-723" data-mp="jpkmewy6qe1r">the whole job.</span>
      </h2>
      <p className={subClass} data-id="element-724" data-mp="3s5mg74sjwwuj">
        One X1 runs up to 14 hours on low and up to 5 hours on high, and recharges over USB-C in about 90 minutes.
      </p>
      <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-725" data-mp="22qbomumqhryk">
        {[{
        label: 'Low',
        value: 'Up to 14 hours'
      }, {
        label: 'High',
        value: 'Up to 5 hours'
      }].map(mode => <div key={mode.label} className="flex items-center gap-4 border border-white/12 bg-ink-900 px-5 py-5" data-id="element-726" data-mp="1tpx5a0tbymxp">
            <BatteryFullIcon className="h-8 w-8 shrink-0 text-grit-500" aria-hidden="true" data-id="element-727" data-mp="1ynoxyx1sud2y" />
            <div data-id="element-728" data-mp="2mdsyuwnlryq9">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-729" data-mp="2mfdtsndtxjon">{mode.label}</p>
              <p className="font-display text-2xl font-bold uppercase text-offwhite" data-id="element-730" data-mp="2qf4ficoi19mb">{mode.value}</p>
            </div>
          </div>)}
      </div>
      <p className={statementClass} data-id="element-731" data-mp="2hepu7dbjbuit">
        Running high all shift?
        <br data-id="element-732" data-mp="2hh2j6afpn55d" />
        Keep a second X1 charged and swap.
      </p>
      <p className="mt-3 text-[12px] text-steel-500" data-id="element-733" data-mp="10zyb5r0a3zm6">Runtime varies by light mode, battery age, temperature and use.</p>
    </>;
}
function FitFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-734" data-mp="3g7al476o3ibd">
        You&apos;ll forget
        <br data-id="element-735" data-mp="3g9wu8b97i5vp" />
        <span className="text-grit-500" data-id="element-736" data-mp="3l6ds3x67jylz">you&apos;re wearing it.</span>
      </h2>
      <p className={subClass} data-id="element-737" data-mp="3l8gg6bnsvho2">The light is built into the frame, so there is nothing strapped around your head.</p>
      <Bullets items={['1.4 oz (40 g), lighter than most safety glasses', 'No head strap, no battery pack on your neck', 'Wave-on motion sensor. No button to hunt for with gloves on']} data-id="element-738" data-mp="3bgu4yfbgpilb" />
    </>;
}
function ShippingFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-739" data-mp="2nj4ytfzytl9f">
        Shipping,
        <br data-id="element-740" data-mp="2nkg6hqwj7d5t" />
        <span className="text-grit-500" data-id="element-741" data-mp="2nkznn5ecxxis">straight answers.</span>
      </h2>
      <div className="mt-8 grid gap-4 lg:grid-cols-2" data-id="element-742" data-mp="2sirgc1mttno1">
        <div className="border-2 border-grit-500 bg-grit-500/10 p-6 lg:col-span-2" data-id="element-743" data-mp="lrryl48y01jp">
          <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-744" data-mp="qqlais8kzkhr">
            <TruckIcon className="h-4 w-4" aria-hidden="true" data-id="element-745" data-mp="2b8dyzsn2b4dn" />
            Every order
          </p>
          <p className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-offwhite" data-id="element-746" data-mp="269sw1lgfe6li">Free U.S. shipping</p>
          <p className="mt-2 text-[15px] text-steel-300" data-id="element-747" data-mp="2kziequkkat9j">
            X1 Essential builds from $29.95, the Complete System and every multi-pack ship free.
          </p>
        </div>
      </div>
      <p className="mt-4 text-[14px] text-steel-400" data-id="element-748" data-mp="2tyc728qv0xha">
        USPS, insured and tracked, from the U.S. Orders placed before 2pm CT ship the same business day.
      </p>
      <CtaButton href="#build-your-x1" className="mt-8 w-full sm:w-auto" onClick={() => trackCustom('mini_funnel_cta_clicked')} data-id="element-749" data-mp="33xb4ev66d6oz">
        Build your X1 — from $29.95
      </CtaButton>
    </>;
}
function WarrantyFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-750" data-mp="19k37mxyn2l0p">
        Break it.
        <br data-id="element-751" data-mp="14k75l49i53ns" />
        <span className="text-grit-500" data-id="element-752" data-mp="14kql6ebojfyn">We replace it.</span>
      </h2>
      <div className="mt-8 flex flex-col items-start gap-6 border border-grit-500/50 bg-ink-900 p-6 sm:flex-row sm:items-center sm:p-8" data-id="element-753" data-mp="14mjvc3li4ibe">
        <LifetimeSeal className="h-24 w-24 shrink-0" data-id="element-754" data-mp="1y967gqpfpqsc" />
        <dl className="grid flex-1 gap-3 text-[15px]" data-id="element-755" data-mp="1ybrb9l9amgsq">
          <div data-id="element-756" data-mp="237zj0dl5v7pv">
            <dt className="font-display text-xl font-bold uppercase text-offwhite" data-id="element-757" data-mp="237g3t4mwaxfj">Single X1</dt>
            <dd className="text-steel-300" data-id="element-758" data-mp="289oyq6jbw9ka">One-year replacement coverage, or add the Lifetime Replacement Warranty.</dd>
          </div>
          <div data-id="element-759" data-mp="2d5dxw8rfrog2">
            <dt className="font-display text-xl font-bold uppercase text-offwhite" data-id="element-760" data-mp="2h8k5pku75non">Every multi-pack</dt>
            <dd className="text-steel-300" data-id="element-761" data-mp="2lz0id2q7159y">Lifetime Replacement Warranty on every X1 in the pack.</dd>
          </div>
          <div data-id="element-762" data-mp="2qxk535cm2l3d">
            <dt className="font-display text-xl font-bold uppercase text-offwhite" data-id="element-763" data-mp="2qxagnm77slzs">Making a claim</dt>
            <dd className="text-steel-300" data-id="element-764" data-mp="2vz9iusr7xcv5">
              One email with a photo. The replacement unit is free; a flat $6.95 shipping and handling fee applies per
              approved replacement claim.
            </dd>
          </div>
        </dl>
      </div>
      <Link to="/legal/guarantee" className="mt-4 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-grit-500 underline-offset-4 hover:underline" data-id="element-765" data-mp="k9tldcoaonui">
        View warranty terms
        <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-766" data-mp="adgpq996vzx4" />
      </Link>
    </>;
}
const recommendations: {
  label: string;
  body: string;
  pack: PackId | 'business';
  result: string;
}[] = [{
  label: 'Just for me',
  body: 'One X1 for your own work.',
  pack: 'single',
  result: '1 X1 · $49.95'
}, {
  label: 'One for work + backups',
  body: 'A spare charged and a spare in the truck.',
  pack: 'trio',
  result: '3 X1 · $99.90'
}, {
  label: 'Small crew',
  body: 'Outfit a few people at once.',
  pack: 'crew',
  result: '6 X1 · $179.90'
}, {
  label: 'Business / team',
  body: 'One order for the whole crew.',
  pack: 'business',
  result: '30 X1 · $699.90'
}];
function PackRecommender() {
  return <>
      <h2 className={headlineClass} data-id="element-767" data-mp="16gvx6h05wcdv">
        Who is it
        <br data-id="element-768" data-mp="1bgifh4uukitj" />
        <span className="text-grit-500" data-id="element-769" data-mp="1bh1wmjcob36i">for?</span>
      </h2>
      <p className={subClass} data-id="element-770" data-mp="1befe5oo8v39i">Pick the one that sounds like you and we&apos;ll take you to that pack.</p>
      <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-771" data-mp="wlvmbk6joi7d">
        {recommendations.map(item => <li key={item.label} data-id="element-772" data-mp="1p8ggstibhaf5">
            <a href={item.pack === 'business' ? '#company' : '#offer'} data-buy-intent="true" onClick={event => {
          trackCustom('pack_recommendation_selected', {
            choice: item.label,
            pack: item.pack
          });
          if (item.pack === 'business') {
            event.preventDefault();
            scrollToCompany();
            return;
          }
          event.preventDefault();
          requestOffer(item.pack);
        }} className="flex h-full flex-col border border-white/15 bg-ink-900 p-5 transition-[border-color,background-color] duration-150 ease-grit hover:border-grit-500 hover:bg-grit-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-773" data-mp="1p981386grlq4">
              <span className="font-display text-xl font-bold uppercase leading-tight text-offwhite" data-id="element-774" data-mp="b7k1koafa1y4">{item.label}</span>
              <span className="mt-1.5 text-[14px] text-steel-300" data-id="element-775" data-mp="6a0kzhq95d30">{item.body}</span>
              <span className="mt-auto flex items-center justify-between gap-2 pt-4 font-display text-lg font-bold uppercase text-grit-500" data-id="element-776" data-mp="3eq3joxzdzrf7">
                {item.result}
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-777" data-mp="3nqi9oanapup1" />
              </span>
            </a>
          </li>)}
      </ul>
    </>;
}
function CrewFunnel() {
  return <>
      <h2 className={headlineClass} data-id="element-778" data-mp="12z88uwecfya3">
        One order.
        <br data-id="element-779" data-mp="131ugew18idsb" />
        <span className="text-grit-500" data-id="element-780" data-mp="131m1kyljwyeh">The whole crew.</span>
      </h2>
      <p className={subClass} data-id="element-781" data-mp="yxwiuprk5b1d">
        Every X1 in a multipack is a Complete System, with Lifetime Replacement Warranty on every unit and free standard shipping.
      </p>
      <div className="mt-8 grid gap-4 lg:grid-cols-2" data-id="element-782" data-mp="lagmz47tfms">
        <a href="#offer" data-buy-intent="true" onClick={event => {
        event.preventDefault();
        requestOffer('crew');
        trackCustom('mini_funnel_cta_clicked', {
          pack: 'crew'
        });
      }} className="border border-white/15 bg-ink-900 p-6 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-783" data-mp="5lgb0n1vdg8k">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-784" data-mp="3dz5glzzvwsfu">Buy 4, get 2 free</p>
          <p className="mt-2 font-display text-3xl font-extrabold uppercase text-offwhite" data-id="element-785" data-mp="3i10hj3dwl3t3">6 X1 · $179.90</p>
          <p className="mt-1 text-[14px] text-steel-300" data-id="element-786" data-mp="2qbnlbuhevc84">$29.98 per X1 · 18 lenses</p>
        </a>
        <a href="#company" data-buy-intent="true" onClick={event => {
        event.preventDefault();
        trackCustom('mini_funnel_cta_clicked', {
          pack: 'business'
        });
        scrollToCompany();
      }} className="border-2 border-grit-500 bg-grit-500/10 p-6 transition-colors duration-150 ease-grit hover:bg-grit-500/15" data-id="element-787" data-mp="2ud923qx2ganl">
          <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-788" data-mp="2duw6iku3jgft">
            <BuildingIcon className="h-4 w-4" aria-hidden="true" data-id="element-789" data-mp="23ulw17zg4n4c" />
            Business / crew pack
          </p>
          <p className="mt-2 font-display text-3xl font-extrabold uppercase text-offwhite" data-id="element-790" data-mp="1z3nfc7ajpn0k">30 X1 · $699.90</p>
          <p className="mt-1 text-[14px] text-steel-300" data-id="element-791" data-mp="1u4ant98qbrrn">Buy 20, get 10 free · 90 lenses · one order</p>
        </a>
      </div>
    </>;
}
const closingCta: Partial<Record<ObjectionId, {
  pack: PackId;
  cta: string;
}>> = {
  battery: {
    pack: 'trio',
    cta: 'See multi-X1 options'
  },
  fit: {
    pack: 'single',
    cta: 'Choose your X1'
  },
  warranty: {
    pack: 'trio',
    cta: 'Choose your coverage'
  }
};

/**
 * One dynamic recovery area, hidden until the visitor tells us what is holding
 * them back. Only the matching answer renders, so the normal page stays clean.
 */
export function ObjectionRecovery({
  active,
  onChooseAnother
}: ObjectionRecoveryProps) {
  const choice = active ? objectionChoices.find(item => item.id === active) : undefined;
  useEffect(() => {
    if (choice) trackCustom('mini_funnel_viewed', {
      objection: choice.id
    });
  }, [choice]);
  const close = choice ? closingCta[choice.id] : undefined;
  return <AnimatePresence initial={false} data-id="element-792" data-mp="2q3sozvy8375x">
      {choice && <motion.section id={RECOVERY_ID} key={choice.id} aria-labelledby="recovery-heading" className="scroll-mt-28 border-b border-white/10 bg-ink-950" initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 12
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-793" data-mp="2cbcsldztu7dz">
          <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-794" data-mp="1npon61c72coa">
            <p className="inline-flex flex-wrap items-center gap-2 border border-grit-500/60 bg-grit-500/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-grit-500" data-id="element-795" data-mp="1iqv7q3dckblh">
              You asked about:
              <span className="text-offwhite" data-id="element-796" data-mp="1sohybn40drla">{choice.label}</span>
            </p>

            <div id="recovery-heading" className="mt-6" data-id="element-797" data-mp="26oizl3o6r11a">
              {choice.id === 'price' && <PriceFunnel data-id="element-798" data-mp="26mg1dwcvq1lh" />}
              {choice.id === 'prescription' && <PrescriptionFunnel data-id="element-799" data-mp="21oxwqi5wa8ja" />}
              {choice.id === 'battery' && <BatteryFunnel data-id="element-800" data-mp="2gf570alr0l8w" />}
              {choice.id === 'fit' && <FitFunnel data-id="element-801" data-mp="2ggop86qyt6db" />}
              {choice.id === 'shipping' && <ShippingFunnel data-id="element-802" data-mp="2bgk7kdebm5li" />}
              {choice.id === 'warranty' && <WarrantyFunnel data-id="element-803" data-mp="2pfa51u51y7ae" />}
              {choice.id === 'pack' && <PackRecommender data-id="element-804" data-mp="2pftmzaupcvof" />}
              {choice.id === 'crew' && <CrewFunnel data-id="element-805" data-mp="2le8es2t455kk" />}
            </div>

            {close && <PackClose recommended={close.pack} cta={close.cta} data-id="element-806" data-mp="2lblwb84op5nk" />}

            <button type="button" onClick={onChooseAnother} className="mt-8 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-steel-400 transition-colors duration-150 ease-grit hover:text-grit-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-807" data-mp="2xvdiudrwsl2o">
              <RefreshCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-808" data-mp="2e8fozo11kumw" />
              Something else? Choose another
            </button>
          </div>
        </motion.section>}
    </AnimatePresence>;
}